import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { COMPONENT_QUERY, COMPONENT_QUERY_COMPARE } from '../../../constants/api';
import { TIME_PERIOD, WEEK_SUBSTRACTION_VALUE } from '../../../constants/datePickerConstants';
import api from '../../../services/api';
import {
  WEEKLY_MATCHBACK_DATA,
  YTD_MATCHBACK_ROMI,
  CHANNEL_OPPS,
  CHANNEL_OPPS_WITHOUT_LOCAL,
  OPPS_SPEND_CHANNEL_VENDOR_LEAD_AGG,
  OPPS_SPEND_CHANNEL_VENDOR_SEM,
  SEM_CHANNEL_FILTER_VALUES,
  YOY_CHANNEL_PERFORMANCE_FILTER,
  YOY_OPPS_SPEND_BY_CHANNEL,
} from '../constants';
import {
  addTotalObject,
  addMatchbackTotalObject,
  getTransformedMatchbackRows,
  sortByChannelAndVendor,
  transformYTDMatchbackRomiResponseData,
  transformYOYChannelResponseData,
  updateFilterValues,
} from '../services/ChannelOppsServices';
import { channelOppsActions } from '../../../modules/reducer/channelOppsSlice';
import { subBranchDashOppsByChannelActions } from '../../../modules/reducer/subBranchDashOppsByChannelSlice';
import { TransformDate } from '../../../components/DatePicker/DatePickerUtils/datePickerUtils';
import { previousWeekExtractor } from '../../../data/branchPerformanceData';
import selectors from '../../../modules/selectors/datePickerSelectors';
import {
  currentYearAndQuarterFun,
  getFilteredStoreOptions,
  isStoreValid,
} from '../../../constants/utils';

export const useTableDataHook = ({ subBranchDashboard }) => {
  const { userStores, userData } = useSelector((state) => state.auth);
  const location = useLocation();
  const options = useMemo(
    () =>
      getFilteredStoreOptions(userStores, userData?.data, subBranchDashboard, location.pathname),
    [userStores, userData, subBranchDashboard, location.pathname]
  );

  const { channelOppsRange } = useSelector((state) => state.channelOpps);
  const { subDashChannelOppsRange } = useSelector((state) => state.subBranchDashOppsByChannel);
  const { transformedDataWithDate } = useSelector(selectors.datePickerCombinedData);
  const [defaultFilter, setDefaultFilter] = useState(TIME_PERIOD);
  const [timePeriodFilter, setTimePeriodFilter] = useState([]);
  const dispatch = useDispatch();

  // Initialize storeId based on whether it's a sub-branch dashboard
  const [storeId, setStoreId] = useState(null);
  const [debouncedStoreId, setDebouncedStoreId] = useState(null); // For debouncing in multi-select mode
  const debounceTimerRef = useRef(null);
  const [resetStore, setResetStore] = useState(false);
  const [channelOppsRowdata, setChannelOppsRowdata] = useState([]);
  const [semOppsChannelData, setSemOppsChannelData] = useState([]);
  const [semOppsData, setSemOppsData] = useState([]);
  const [leadAggregationOppsData, setLeadAggregationOppsData] = useState([]);
  const [matchbackData, setMatchbackData] = useState([]);
  const [ytdMatchbackRomiData, setYtdMatchbackRomiData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [semOppsChannelDataError, setSemOppsChannelDataError] = useState(null);
  const [semOppsDataError, setSemOppsDataError] = useState(null);
  const [leadAggregationOppsDataError, setLeadAggregationOppsDataError] = useState(null);
  const [matchbackDataError, setMatchbackDataError] = useState(null);
  const [ytdMatchbackRomiError, setYtdMatchbackRomiError] = useState(null);
  const [spendToggleState, setSpendToggleState] = useState(false);
  const [showSpendToggle, setShowSpendToggle] = useState(false);
  const [yoyChannelRowData, setYoyChannelRowData] = useState([]);
  const [yoyChannelLoading, setYoyChannelLoading] = useState(false);
  const [yoyChannelError, setYoyChannelError] = useState(null);
  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const [semOppsChannelDataLoading, setSemOppsChannelDataLoading] = useState(!subBranchDashboard);
  const [isSemOppsChannelReportDownloading, setIsSemOppsChannelReportDownloading] = useState(false);
  const [semOppsDataLoading, setSemOppsDataLoading] = useState(true);
  const [leadAggregationOppsDataLoading, setLeadAggregationOppsDataLoading] = useState(true);
  const [matchbackDataLoading, setMatchbackDataLoading] = useState(!subBranchDashboard);
  const [ytdMatchbackRomiLoading, setYtdMatchbackRomiLoading] = useState(!subBranchDashboard);
  const [isSemOppsReportDownloading, setIsSemOppsReportDownloading] = useState(false);
  const [isLeadAggregationOppsReportDownloading, setIsLeadAggregationOppsReportDownloading] =
    useState(false);
  const initialLoading = useRef(true);
  const currentFiscalYear = Number(userData?.data?.current_year) || new Date().getFullYear();
  const currentFiscalWeekData = useMemo(
    () => previousWeekExtractor(transformedDataWithDate),
    [transformedDataWithDate]
  );
  const semOppsChannelFilters = useMemo(
    () =>
      timePeriodFilter?.length > 0
        ? [
            ...timePeriodFilter,
            {
              col: 'channel_name',
              op: 'IN',
              val: SEM_CHANNEL_FILTER_VALUES,
            },
          ]
        : [],
    [timePeriodFilter]
  );
  const ytdMatchbackRomiFilters = useMemo(() => {
    if (!debouncedStoreId || subBranchDashboard || !currentFiscalWeekData?.currentWeek) {
      return [];
    }

    return [
      {
        col: 'fiscal_year',
        op: 'in',
        val: [currentFiscalYear - 1, currentFiscalYear],
      },
      {
        col: 'fiscal_week',
        op: '<=',
        val: currentFiscalWeekData.currentWeek,
      },
      {
        col: 'store_id',
        op: 'IN',
        val: Array.isArray(debouncedStoreId) ? debouncedStoreId : [debouncedStoreId],
      },
    ];
  }, [currentFiscalWeekData?.currentWeek, currentFiscalYear, debouncedStoreId, subBranchDashboard]);

  // Initialize storeId when options are loaded
  useEffect(() => {
    if (options?.length > 0) {
      const initialValue = subBranchDashboard ? options.map((opt) => opt.value) : options[0]?.value;

      setStoreId(initialValue);
      setDebouncedStoreId(initialValue); // Initialize debounced value too
    }
  }, [options, subBranchDashboard]);

  const checkForGlobalObject = (object) => {
    if (object === null || object === undefined) {
      return false; // Return false if object is null or undefined
    }
    return (
      Object.values(object).every((value) => value === null || value === undefined) ||
      Object.values(object).some((value) => value === null || value === undefined)
    );
  };

  useEffect(() => {
    timePeriodRangeHandler();
    quarterAndYearValueHandler();
  }, [transformedDataWithDate, channelOppsRange, subDashChannelOppsRange]);

  // Updated to handle both single store ID and array of store IDs with debouncing
  const handleStoreIdChange = useCallback(
    (store) => {
      if (subBranchDashboard) {
        // For sub-branch dashboard, use debouncing
        const newValue = Array.isArray(store) ? store : [store];

        // Update the UI immediately
        setStoreId(newValue);

        // Clear any existing timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Set a new timer for the debounced update
        debounceTimerRef.current = setTimeout(() => {
          setDebouncedStoreId(newValue);
        }, 500); // 500ms debounce delay
      } else {
        // For regular dashboard, update immediately without debouncing
        setStoreId(store);
        setDebouncedStoreId(store);
      }
    },
    [subBranchDashboard]
  );

  // Clean up timer on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    []
  );

  const handleResetForStore = () => {
    setResetStore(true);
  };

  const timePeriodRangeHandler = () => {
    const subBranchDashBoardRangeValues = subBranchDashboard
      ? subDashChannelOppsRange
      : channelOppsRange;
    const allValuesAreNull = checkForGlobalObject(subBranchDashBoardRangeValues);
    const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);

    if (allValuesAreNull && obj && Object.keys(obj).length && transformedDataWithDate?.length > 0) {
      const quarterValue =
        transformedDataWithDate?.length &&
        TransformDate?.quickFilters?.TWELVE_WEEK_DATA(
          transformedDataWithDate,
          previousWeekExtractor,
          WEEK_SUBSTRACTION_VALUE
        );

      !subBranchDashboard && dispatch(channelOppsActions.setChannelOppsRange(quarterValue));
      subBranchDashboard &&
        dispatch(subBranchDashOppsByChannelActions.setSubDashChannelOppsRange(quarterValue));
      const value = `w${subBranchDashBoardRangeValues?.start_week}, ${subBranchDashBoardRangeValues?.start_year} - w${subBranchDashBoardRangeValues?.end_week}, ${subBranchDashBoardRangeValues?.end_year}`;

      setDefaultFilter(value);
    }
  };

  const quarterAndYearValueHandler = () => {
    if (transformedDataWithDate?.length > 0) {
      const currentYearAndQuarter = currentYearAndQuarterFun(
        transformedDataWithDate,
        previousWeekExtractor,
        WEEK_SUBSTRACTION_VALUE,
        TransformDate
      );

      !subBranchDashboard &&
        dispatch(channelOppsActions.setCurrentQuarter(currentYearAndQuarter?.currentQuarterValue));
      !subBranchDashboard &&
        dispatch(channelOppsActions.setCurrentYear(currentYearAndQuarter?.currentYearBasedOnWeek));
      subBranchDashboard &&
        dispatch(
          subBranchDashOppsByChannelActions.setCurrentQuarter(
            currentYearAndQuarter?.currentQuarterValue
          )
        );
      subBranchDashboard &&
        dispatch(
          subBranchDashOppsByChannelActions.setCurrentYear(
            currentYearAndQuarter?.currentYearBasedOnWeek
          )
        );
    }
  };

  const getPayload = (key, timePeriodFilter) => {
    const payload = {
      [key]: {
        filters: timePeriodFilter,
      },
    };

    return payload;
  };

  /**
   * @param payload
   * @param key
   */
  async function fetchOppsByChannel(payload, key) {
    const response = await api.post(`${COMPONENT_QUERY}?component=${key}`, payload);

    return response.data;
  }

  const getChannelOppsComponentKey = () =>
    spendToggleState ? CHANNEL_OPPS_WITHOUT_LOCAL : CHANNEL_OPPS;

  const fetchData = async () => {
    try {
      setError(null);
      setIsLoading(true);
      const key = getChannelOppsComponentKey();
      const payload = getPayload(key, timePeriodFilter);
      const result = await fetchOppsByChannel(payload, key);

      setChannelOppsRowdata(
        result?.data?.[key]?.result?.length > 0 ? addTotalObject(result?.data?.[key]?.result) : []
      );
    } catch (error) {
      console.error(error);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSemOppsChannelData = async () => {
    setSemOppsChannelDataLoading(true);
    setSemOppsChannelDataError(null);
    setSemOppsChannelData([]);
    try {
      const keySemChannels = getChannelOppsComponentKey();
      const payload = getPayload(keySemChannels, semOppsChannelFilters);
      const result = await fetchOppsByChannel(payload, keySemChannels);

      setSemOppsChannelData(
        result?.data?.[keySemChannels]?.result?.length > 0
          ? addTotalObject(result?.data?.[keySemChannels]?.result)
          : []
      );
    } catch (err) {
      setSemOppsChannelDataError(true);
    } finally {
      setSemOppsChannelDataLoading(false);
    }
  };

  const fetchSemOppsData = async () => {
    setSemOppsDataLoading(true);
    setSemOppsDataError(null);
    setSemOppsData([]);
    try {
      const keyVendorsSEM = OPPS_SPEND_CHANNEL_VENDOR_SEM;
      const payload = getPayload(keyVendorsSEM, timePeriodFilter);
      const result = await fetchOppsByChannel(payload, keyVendorsSEM);

      setSemOppsData(
        result?.data?.[keyVendorsSEM]?.result?.length > 0
          ? addTotalObject(sortByChannelAndVendor(result?.data?.[keyVendorsSEM]?.result))
          : []
      );
    } catch (err) {
      setSemOppsDataError(true);
    } finally {
      setSemOppsDataLoading(false);
    }
  };

  const fetchLeadAggregationOppsData = async () => {
    setLeadAggregationOppsDataLoading(true);
    setLeadAggregationOppsDataError(null);
    setLeadAggregationOppsData([]);
    try {
      const keyLeadAggregation = OPPS_SPEND_CHANNEL_VENDOR_LEAD_AGG;
      const payload = getPayload(keyLeadAggregation, timePeriodFilter);
      const result = await fetchOppsByChannel(payload, keyLeadAggregation);

      setLeadAggregationOppsData(
        result?.data?.[keyLeadAggregation]?.result?.length > 0
          ? addTotalObject(
              sortByChannelAndVendor(result?.data?.[keyLeadAggregation]?.result),
              'Vendor'
            )
          : []
      );
    } catch (err) {
      setLeadAggregationOppsDataError(true);
    } finally {
      setLeadAggregationOppsDataLoading(false);
    }
  };

  const fetchMatchbackData = async () => {
    setMatchbackDataLoading(true);
    setMatchbackDataError(null);
    setMatchbackData([]);

    try {
      const keyMatchback = WEEKLY_MATCHBACK_DATA;
      const payload = getPayload(keyMatchback, timePeriodFilter);
      const result = await fetchOppsByChannel(payload, keyMatchback);
      const transformedMatchbackData = getTransformedMatchbackRows(
        result?.data?.[keyMatchback]?.result
      );

      setMatchbackData(
        transformedMatchbackData?.length > 0
          ? addMatchbackTotalObject(transformedMatchbackData)
          : []
      );
    } catch (err) {
      setMatchbackDataError(true);
    } finally {
      setMatchbackDataLoading(false);
    }
  };

  const fetchYtdMatchbackRomiData = async () => {
    setYtdMatchbackRomiLoading(true);
    setYtdMatchbackRomiError(null);
    setYtdMatchbackRomiData([]);

    try {
      const keyYtdMatchbackRomi = YTD_MATCHBACK_ROMI;
      const payload = getPayload(keyYtdMatchbackRomi, ytdMatchbackRomiFilters);
      const result = await fetchOppsByChannel(payload, keyYtdMatchbackRomi);

      setYtdMatchbackRomiData(
        transformYTDMatchbackRomiResponseData(
          result?.data?.[keyYtdMatchbackRomi]?.result,
          currentFiscalYear
        )
      );
    } catch (err) {
      setYtdMatchbackRomiError(true);
    } finally {
      setYtdMatchbackRomiLoading(false);
    }
  };

  // Update filter to handle multiple store IDs - Use debouncedStoreId instead of storeId
  useEffect(() => {
    if (!debouncedStoreId) {
      return;
    }

    setShowSpendToggle(isStoreValid(debouncedStoreId, options, subBranchDashboard));
    if (subBranchDashboard && !isStoreValid(debouncedStoreId, options, subBranchDashboard)) {
      setSpendToggleState(false);
    }
    const selectedRangeValues = subBranchDashboard ? subDashChannelOppsRange : channelOppsRange;

    if (!checkForGlobalObject(selectedRangeValues)) {
      setTimePeriodFilter([
        {
          col: 'dateindex',
          op: '>=',
          val: selectedRangeValues?.start_year
            ?.toString()
            ?.concat(
              selectedRangeValues?.start_week <= 9 && selectedRangeValues?.start_week > 0
                ? '0'.concat(selectedRangeValues?.start_week)
                : selectedRangeValues?.start_week
            ),
        },
        {
          col: 'dateindex',
          op: '<=',
          val: selectedRangeValues?.end_year
            ?.toString()
            ?.concat(
              selectedRangeValues?.end_week <= 9 && selectedRangeValues?.end_week > 0
                ? '0'.concat(selectedRangeValues?.end_week)
                : selectedRangeValues?.end_week
            ),
        },
        {
          col: 'store_id',
          op: 'IN',
          val: Array.isArray(debouncedStoreId) ? debouncedStoreId : [debouncedStoreId],
        },
      ]);
    }
  }, [debouncedStoreId, channelOppsRange, subDashChannelOppsRange]);

  useEffect(() => {
    setIsLoading(true);
    if (timePeriodFilter?.length > 0) {
      fetchData();
    }
  }, [timePeriodFilter, spendToggleState]);

  useEffect(() => {
    if (subBranchDashboard) {
      setSemOppsChannelData([]);
      setSemOppsChannelDataError(null);
      setSemOppsChannelDataLoading(false);
      return;
    }

    setSemOppsChannelDataLoading(true);
    if (semOppsChannelFilters?.length > 0) {
      fetchSemOppsChannelData();
    }
  }, [semOppsChannelFilters, spendToggleState, subBranchDashboard]);

  useEffect(() => {
    setSemOppsDataLoading(true);
    if (timePeriodFilter?.length > 0) {
      fetchSemOppsData();
    }
  }, [timePeriodFilter]);

  useEffect(() => {
    setLeadAggregationOppsDataLoading(true);
    if (timePeriodFilter?.length > 0) {
      fetchLeadAggregationOppsData();
    }
  }, [timePeriodFilter]);

  useEffect(() => {
    if (subBranchDashboard) {
      setMatchbackData([]);
      setMatchbackDataError(null);
      setMatchbackDataLoading(false);
      return;
    }

    setMatchbackDataLoading(true);
    if (timePeriodFilter?.length > 0) {
      fetchMatchbackData();
    }
  }, [timePeriodFilter, subBranchDashboard]);

  useEffect(() => {
    if (subBranchDashboard) {
      setYtdMatchbackRomiData([]);
      setYtdMatchbackRomiError(null);
      setYtdMatchbackRomiLoading(false);
      return;
    }

    setYtdMatchbackRomiLoading(true);
    if (ytdMatchbackRomiFilters?.length > 0) {
      fetchYtdMatchbackRomiData();
    }
  }, [currentFiscalYear, subBranchDashboard, ytdMatchbackRomiFilters]);

  // Update YoY fetch to handle multiple stores - use debouncedStoreId
  const fetchYoYChannelPerformance = async (key, currentWeekData) => {
    try {
      setYoyChannelLoading(true);
      initialLoading.current = false;
      setYoyChannelError(null);

      // If in sub-branch dashboard with multiple stores selected, use the first one for YoY data
      const storeIdForYoY =
        Array.isArray(debouncedStoreId) && debouncedStoreId.length > 0
          ? debouncedStoreId[0]
          : debouncedStoreId;

      const payload = {
        ...YOY_CHANNEL_PERFORMANCE_FILTER,
        current_query_builder: {
          ...YOY_CHANNEL_PERFORMANCE_FILTER.current_query_builder,
          filters: updateFilterValues(
            YOY_CHANNEL_PERFORMANCE_FILTER.current_query_builder.filters,
            currentWeekData.currentYear,
            currentWeekData.currentWeek,
            storeIdForYoY
          ),
        },
        previous_query_builder: {
          ...YOY_CHANNEL_PERFORMANCE_FILTER.previous_query_builder,
          filters: updateFilterValues(
            YOY_CHANNEL_PERFORMANCE_FILTER.previous_query_builder.filters,
            currentWeekData.currentYear - 1,
            currentWeekData.currentWeek,
            storeIdForYoY
          ),
        },
      };

      const response = await api.post(`${COMPONENT_QUERY_COMPARE}?component=${key}`, payload);
      const transformedData = transformYOYChannelResponseData(response.data.data);

      setYoyChannelRowData(transformedData);
    } catch (error) {
      setYoyChannelError(true);
    } finally {
      setYoyChannelLoading(false);
    }
  };

  // Update to check for valid debouncedStoreId value
  useEffect(() => {
    if (!debouncedStoreId) {
      return;
    }

    // Skip YoY data fetching entirely when in subBranchDashboard mode
    if (subBranchDashboard) {
      return;
    }
    const hasValidStoreId = Array.isArray(debouncedStoreId)
      ? debouncedStoreId.length > 0
      : Boolean(debouncedStoreId);

    if (hasValidStoreId && currentFiscalWeekData?.currentYear && !yoyChannelLoading) {
      fetchYoYChannelPerformance(YOY_OPPS_SPEND_BY_CHANNEL, currentFiscalWeekData);
    }
  }, [currentFiscalWeekData, debouncedStoreId, subBranchDashboard]);

  return {
    options,
    defaultFilter,
    resetStore,
    handleResetForStore,
    handleStoreIdChange,
    channelOppsRowdata,
    semOppsChannelData,
    isLoading,
    error,
    semOppsChannelDataError,
    spendToggleState,
    setSpendToggleState,
    storeId, // Return the immediate storeId for the UI
    showSpendToggle,
    userData,
    yoyChannelRowData,
    yoyChannelLoading,
    yoyChannelError,
    initialLoading,
    setIsReportDownloading,
    isReportDownloading,
    timePeriodFilter,
    semOppsChannelFilters,
    semOppsChannelDataLoading,
    isSemOppsChannelReportDownloading,
    setIsSemOppsChannelReportDownloading,
    semOppsData,
    semOppsDataLoading,
    semOppsDataError,
    leadAggregationOppsData,
    setLeadAggregationOppsData,
    leadAggregationOppsDataLoading,
    setLeadAggregationOppsDataLoading,
    leadAggregationOppsDataError,
    setLeadAggregationOppsDataError,
    matchbackData,
    matchbackDataLoading,
    matchbackDataError,
    ytdMatchbackRomiData,
    ytdMatchbackRomiLoading,
    ytdMatchbackRomiError,
    isSemOppsReportDownloading,
    setIsSemOppsReportDownloading,
    isLeadAggregationOppsReportDownloading,
    setIsLeadAggregationOppsReportDownloading,
  };
};
